import { AdminAppShell } from '@/components/admin/admin-rail';
import { LoginCard } from '@/components/admin/login-card';
import { AtelierBanner } from '@/components/admin/prod/atelier-banner';
import { VitrineBanner } from '@/components/admin/vitrine/vitrine-banner';
import { VitrineMark } from '@/components/admin/vitrine/vitrine-mark';
import { ADMIN_DOOR_WORDING, adminRefusalWording } from '@/lib/admin-labels';
import { doorForPath } from '@/lib/admin-nav';
import { ADMIN_SESSION_KEY, type AdminSession, AdminSessionProvider } from '@/lib/admin-session';
import { fetchAdminSessionStatus, openAdminSession } from '@/lib/api';
import { ATELIER_INSTANCE, InstanceProvider, useInstance, useIsVitrine } from '@/lib/use-instance';
import { vitrineRedirect } from '@/lib/vitrine';
import { Logo, PageLayout } from '@pms-digital/design-system';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, createFileRoute, useRouterState } from '@tanstack/react-router';
import { useCallback, useMemo, useState } from 'react';

/**
 * The shell of the administration screens (tableau de bord spec §4, A1/A3; the
 * account door of auth Tahiti §6; the rail of issue #24).
 *
 * A LAYOUT route: the plugin nests by path prefix, so the files in
 * `routes/admin/` are its children without moving or being renamed. That is the
 * whole point of doing it this way - `/admin/sessions?session=xxx` is meant to be
 * pasted into a team thread (transcript admin §6) and the e2e specs go straight
 * to their URLs. Neither notices this file exists.
 *
 * It owns exactly three things: the session, once for every screen; the
 * navigation; and the width of the page. It deliberately renders NO title -
 * each screen keeps its own `<h1>` and its own opening sentence, which say
 * different things that no shared heading would replace.
 *
 * ## What the door became
 *
 * It used to hold a shared token in `sessionStorage` and decide on its own
 * whether to show the screens. It cannot any more: the credential is an
 * `httpOnly` cookie, invisible to this code by construction. So the shell ASKS
 * - `GET /api/admin/session` - and the three answers are the three states:
 *
 * - **200 with an address**: an operator is connected, named in the bar.
 * - **200 with `null`**: the caller came in by the SHARED TOKEN, which names a
 *   machine and not a person (T6/T8). The screens open, with nobody to name.
 *   This is how the e2e specs and the capture scripts get in - all but the two
 *   that test the door itself, which clear the header to be refused.
 * - **401**: nobody is connected. The door.
 *
 * ## Which server this is (docs/specs/vitrine.md section 7, issue #192)
 *
 * `AdminLayout`, the route's component, first asks `GET /api/instance` and holds
 * the answer in a context for every screen (`lib/use-instance.tsx`); the shell
 * proper, `AdminShell`, is what it always was. It waits for the answer before
 * drawing anything, exactly as the shell waits for the session: a vitrine must
 * not flash the atelier's gestures for the length of a request. On the vitrine
 * the shell paints the mark « Vitrine · v1.3.0 » in the bar and the banner above
 * the screens of a model, and sends the three tabs, the four « nouveau »
 * entries and « État de la prod » that do not exist there back to the screen
 * they belong to (`vitrineRedirect`). On the atelier it paints none of that - and
 * paints, where a prod is linked, the banner of the atelier above everything
 * (`AtelierBanner`, §9.3).
 *
 * ## The rail on the left, and one line left at the top
 *
 * Issue #24 moved the navigation out of the header and into a vertical rail
 * (`admin-rail.tsx`), which folds into a bottom tab bar below 768 px. What is
 * left of the header is ONE line - the brand, and who is connected.
 *
 * The header survives rather than dying with the tabs, and that was decided
 * rather than inherited: the alternative put the identity behind a hover
 * tooltip on the rail's sign-out icon, which is not an answer on a touch
 * device, where no tooltip exists. It also kept `--spacing-admin-bar` alive as
 * a token, so halving the bar was a one-value edit and the ten surfaces that
 * anchor on it stayed correct without a line moving.
 *
 * ## Why this chrome is here and not in `__root.tsx`
 *
 * The root shell centres a 160 px logo above a reading column. That is the
 * person's chrome, and the operator gets another one. The root therefore steps
 * aside under `/admin` (one line, there) and this file paints the whole window
 * - the door included, which nothing else would frame.
 */

export const Route = createFileRoute('/admin')({
  component: AdminLayout,
});

function AdminLayout() {
  const instance = useInstance();
  if (instance.isPending) return <PendingFrame />;
  // An answer that cannot be read is the atelier (`lib/use-instance.tsx`).
  return (
    <InstanceProvider value={instance.data ?? ATELIER_INSTANCE}>
      <AdminShell />
    </InstanceProvider>
  );
}

function AdminShell() {
  const pathname = useRouterState({ select: (state) => state.location.pathname });
  const vitrine = useIsVitrine();
  const gone = vitrine ? vitrineRedirect(pathname) : null;
  const queryClient = useQueryClient();

  /**
   * What the door has to say, already worded - or `null` for silence.
   *
   * A string and not the old boolean, because there are now three things to say
   * and they are not the same kind of thing: silence is « on n'a jamais
   * demandé », a refusal is « ce que vous avez tapé n'est pas ça », and an
   * expiry is neither - nobody mistyped anything.
   */
  const [notice, setNotice] = useState<string | null>(null);
  /**
   * A screen was refused while the session query still holds a success.
   *
   * Kept beside the query rather than written into it: the query's job is to
   * say what the backend answered, and overwriting that with a local guess
   * would make a refetch silently undo the refusal.
   */
  const [rejected, setRejected] = useState(false);

  const status = useQuery({
    queryKey: ADMIN_SESSION_KEY,
    queryFn: fetchAdminSessionStatus,
    // A 401 is an ANSWER here, not a hiccup: retrying it three times would put
    // three seconds of blank screen between an operator and the door.
    retry: false,
  });

  const login = useMutation({
    mutationFn: ({ email, password }: { email: string; password: string }) =>
      openAdminSession(email, password),
    onSuccess: (view) => {
      setRejected(false);
      setNotice(null);
      // Written straight in rather than invalidated: the answer we just got IS
      // the status, and a refetch would put a blank frame between the click and
      // the screen for no new information.
      queryClient.setQueryData(ADMIN_SESSION_KEY, { email: view.email });
    },
    onError: (error) => setNotice(adminRefusalWording(error)),
  });

  // Stable: it is a dependency of the effect inside `useRejectOn401`, and a
  // fresh identity on every render would make that effect loop.
  const onRejected = useCallback(() => {
    setRejected(true);
    setNotice(ADMIN_DOOR_WORDING.expired);
  }, []);

  const email = status.data?.email ?? null;
  const connected = status.isSuccess && !rejected;

  const session = useMemo<AdminSession>(() => ({ email, onRejected }), [email, onRejected]);

  if (status.isPending) {
    /*
      Deliberately NOT the door. The status query answers in a few milliseconds,
      and showing the login form for that one frame before swapping it for the
      screens is the worst of both: an operator starts typing into a form that
      is about to vanish. An empty frame of the right shape is honest.
    */
    return <PendingFrame />;
  }

  if (!connected) {
    // The door names the screen it is about to open, read from the catalogue -
    // which is what keeps the e2e specs clicking the same button names they
    // always did (§6, A4).
    const door = doorForPath(pathname);
    return (
      <LoginCard
        title={door.gateTitle}
        action={door.gateAction}
        notice={notice}
        submitting={login.isPending}
        onSubmit={(entered, password) => login.mutate({ email: entered, password })}
      />
    );
  }

  return (
    <AdminSessionProvider value={session}>
      {/*
        The banner of the atelier (§9.3, lot F): ONE thin line above the whole administration, the rail
        included, in the normal flow and NOT sticky - the sticky surfaces below are pinned to
        `--spacing-admin-bar` from the top of the window, and a second piece of furniture up there
        would move ten of them. It draws nothing on the vitrine, nor where no prod is linked.
      */}
      <AtelierBanner />
      {/*
        `md:flex` and nothing below it: on a phone the rail is `position: fixed`
        along the bottom edge, so it is out of flow and the column is the whole
        window. From 768 px up they are two columns of one row.
      */}
      {/*
        `data-admin-shell` is what `styles.css` reads to reserve the height of the sticky header
        (`scroll-padding-top`). The shell is the package `AppShell` (`admin-rail.tsx`): the rail,
        the 56 px header with the official logo and the operator's address, the phone menu.
      */}
      <div data-admin-shell>
        <AdminAppShell>
          {/*
            The page width of the charter for a list or a dashboard (`default`, 1200 px), with its
            margins. Each screen keeps its own `<h1>`, so the layout draws no title.
          */}
          <PageLayout width="default">
            <VitrineMark />
            <VitrineBanner />
            {gone === null ? <Outlet /> : <Navigate to={gone} replace />}
          </PageLayout>
        </AdminAppShell>
      </div>
    </AdminSessionProvider>
  );
}

/**
 * The frame the shell shows while it is still asking who is connected.
 *
 * It was the door's frame too until issue #35 gave the door a scene of its own,
 * and it is kept WHITE rather than moved onto that grey ground - which is a
 * trade-off rather than an oversight, so here is which way it was made.
 *
 * A signed-out operator sees this for a few milliseconds and then the grey
 * scene: one layout flash before a screen they will spend seconds on. A
 * signed-in operator sees this and then the administration window - white, a
 * logo in the same corner, the same shape - and nothing flashes at all. The
 * cookie outlives the tab, eight people open this every day, so the second path
 * is the common one and it is the one that stays smooth.
 *
 * Painting the grey scene here instead would simply swap which of the two
 * flashes; painting nothing at all would flash white either way. What is not an
 * option is showing the form: an operator would start typing into something
 * about to vanish.
 */
function PendingFrame() {
  return (
    <div className="flex min-h-screen flex-col bg-white">
      <header className="flex h-14 items-center px-4">
        <Logo width={180} />
      </header>
    </div>
  );
}
